import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Users, Eye, Clapperboard, Play } from "lucide-react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { API } from "@/constants/roles";
import { MaskedLine } from "@/components/motion/Motion";

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const compact = (n) => new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);

const FRAMES = [
  { id: "0mVXuQB1rqs", tilt: "-rotate-3", pos: "right-[4%] top-[16%]", depth: 26, size: "w-44 lg:w-56" },
  { id: "Wig6LhEWEDE", tilt: "rotate-2", pos: "right-[22%] top-[42%]", depth: 46, size: "w-52 lg:w-64" },
  { id: "9EpEc3UM-pI", tilt: "-rotate-2", pos: "right-[6%] top-[62%]", depth: 34, size: "w-40 lg:w-48" },
];

const thumb = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

export const Hero = () => {
  const [stats, setStats] = useState(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const { scrollY } = useScroll();
  const drift = useTransform(scrollY, [0, 600], [0, -70]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);

  useEffect(() => {
    axios
      .get(`${API}/channel-stats`)
      .then((r) => {
        if (r.data?.configured && r.data?.found) setStats(r.data);
      })
      .catch(() => {});
  }, []);

  const onMouseMove = (e) => {
    const { innerWidth, innerHeight } = window;
    mx.set((e.clientX / innerWidth - 0.5) * 2);
    my.set((e.clientY / innerHeight - 0.5) * 2);
  };

  const statRow = stats
    ? [
        { icon: Users, value: compact(stats.subscribers), label: "Subscribers", testid: "stat-subscribers" },
        { icon: Eye, value: compact(stats.views), label: "Total views", testid: "stat-views" },
        { icon: Clapperboard, value: compact(stats.videos), label: "Videos", testid: "stat-videos" },
      ]
    : [];

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-[100svh] flex-col overflow-hidden pt-28 sm:pt-32"
    >
      <div className="bg-grid-cyan bg-grid-fade absolute inset-0" aria-hidden="true" />
      <div className="absolute -top-40 left-[10%] h-[30rem] w-[30rem] rounded-full bg-cyan-200/30 blur-3xl" aria-hidden="true" />

      {/* Floating clipped frames — the channel's real work, parallaxed */}
      <motion.div style={{ y: drift, opacity: fade }} className="pointer-events-none absolute inset-0 hidden lg:block" aria-hidden="true">
        {FRAMES.map((f, i) => (
          <ParallaxFrame key={f.id} frame={f} sx={sx} sy={sy} index={i} />
        ))}
      </motion.div>

      <div className="relative mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-4 sm:px-6">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex items-center gap-2.5 font-mono-label text-[11px] font-bold uppercase tracking-[0.3em] text-cyan-600"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-500" />
          </span>
          Now hiring · Roblox animation studio
        </motion.p>

        <h1
          data-testid="hero-title"
          className="hero-title-mask mt-7 font-display text-[13.5vw] font-extrabold text-slate-900 sm:text-7xl md:text-8xl lg:text-[7.5rem]"
        >
          <MaskedLine delay={0.25}>Make Roblox</MaskedLine>
          <MaskedLine delay={0.38}>animations</MaskedLine>
          <MaskedLine delay={0.51} className="text-cyan-500">people can't skip.</MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
        >
          Join <span className="font-semibold text-slate-900">@ItsMelto</span> as an Animator, Script Writer, or
          Video Editor. Paid per video, fully remote, and your work in front of a real audience.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-3.5"
        >
          <button
            data-testid="hero-apply-btn"
            onClick={() => scrollTo("apply")}
            className="group flex items-center gap-2 rounded-full bg-cyan-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-cyan-600 hover:shadow-xl hover:shadow-cyan-500/40"
          >
            Apply now
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-explore-roles-button"
            onClick={() => scrollTo("roles")}
            className="rounded-full border border-slate-200 bg-white px-8 py-4 text-base font-semibold text-slate-800 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300 hover:shadow-lg"
          >
            See open roles
          </button>
        </motion.div>

        {statRow.length > 0 && (
          <motion.div
            data-testid="hero-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4"
          >
            {statRow.map(({ icon: Icon, value, label, testid }, i) => (
              <div key={label} data-testid={testid} className={`flex items-center gap-3 ${i > 0 ? "border-l border-slate-200 pl-10" : ""}`}>
                <Icon className="h-4 w-4 text-cyan-500" />
                <div>
                  <p className="font-display text-2xl font-extrabold tracking-tight text-slate-900">{value}</p>
                  <p className="font-mono-label text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</p>
                </div>
              </div>
            ))}
            <p className="flex items-center gap-1.5 font-mono-label text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-500">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live from YouTube
            </p>
          </motion.div>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative mx-auto flex w-full max-w-6xl items-center justify-between px-4 pb-8 sm:px-6"
      >
        <p className="font-mono-label text-[9px] font-bold uppercase tracking-[0.25em] text-slate-400">Scroll — the manifesto</p>
        <div className="flex h-9 w-5 items-start justify-center rounded-full border border-slate-300 p-1.5">
          <span className="scroll-hint-dot h-1.5 w-1.5 rounded-full bg-cyan-500" />
        </div>
      </motion.div>
    </section>
  );
};

const ParallaxFrame = ({ frame, sx, sy, index }) => {
  const x = useTransform(sx, (v) => v * frame.depth);
  const y = useTransform(sy, (v) => v * frame.depth * 0.7);
  return (
    <motion.div
      initial={{ opacity: 0, y: 60, rotate: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.1, delay: 0.7 + index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      style={{ x, y }}
      className={`absolute ${frame.pos}`}
    >
      <div className={`frame-shot ${frame.tilt} ${frame.size}`}>
        <img src={thumb(frame.id)} alt="" loading="eager" className="aspect-video w-full object-cover" />
        <span className="absolute bottom-2 left-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 backdrop-blur">
          <Play className="h-3 w-3 fill-white text-white" />
        </span>
      </div>
    </motion.div>
  );
};
